/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  Dimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { debounce } from 'lodash';
import { LegendList } from '@legendapp/list';

import Colors from '@global/colors';
import { isAndroid, isNative, isMobileWeb } from '@global/screens';
import { ScrollViewRefresher } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import SearchInputMobile from '@components/global/SearchInputMobile';
import Button from '@components/form/Button';
import Pagination from '@components/table/Pagination';

import store from '@/store';
import {
  filterAdded,
  paginationAdded,
  paramsReseted,
  selectParams,
  getCurrentParams,
} from '@/store/params';
import { setSavedFilter } from '@/global/helpers';

// ─── TableMobileInfinite ──────────────────────────────────────────────────────
// Self-scrolling virtualized path for RTK Query infinite queries.
// Height of the app's fixed mobile header, subtracted from the viewport to get the
// definite list height on mobile web (LegendList needs a concrete height there).
const MOBILE_HEADER_HEIGHT = 55;

// NOT exported — used internally when `data` prop is present on TableMobile.
// Owns its own scroll container (LegendList / FlatList); never nested inside
// ScrollViewRefresher, so onEndReached fires reliably.
const TableMobileInfinite = ({
  data,
  renderItem,
  onLoadMore,
  hasNextPage,
  loadingLoadMore,
  loading,
  refetching,
  onRefresh,
  keyExtractor,
  totalData,
  headerComponent,
  searchProps,
  testID,
  filterName,
  searchKey,
  searchValue,
  searchForOptions,
  searchForValue,
  placeholder,
  showSearch = true,
}) => {
  const dispatch = useDispatch();
  const appliedParams = useSelector(selectParams(filterName));
  const insets = useSafeAreaInsets();
  const mobileWebListHeight =
    Dimensions.get('window').height -
    insets.top -
    insets.bottom -
    MOBILE_HEADER_HEIGHT;
  const [refreshing, setRefreshing] = React.useState(false);
  // Measured height of the search bar, subtracted from the (definite) mobile-web
  // list height so the list fits below it without pushing the last rows off-screen.
  const [searchHeight, setSearchHeight] = React.useState(0);

  // Scroll to top on a NEW result set. `refreshTick` is bumped ONLY by filterAdded /
  // paramsReseted (filter / search / reset) — not by pagination (load-more) or a plain
  // refetch (pull-to-refresh handled below; detail-return preserves position). Keyed on
  // refreshTick so it's decoupled from the focus preserve/reset lifecycle.
  const listRef = React.useRef(null);
  const refreshTick = appliedParams?.refreshTick;
  const prevTickRef = React.useRef(refreshTick);
  React.useEffect(() => {
    if (refreshTick !== prevTickRef.current) {
      prevTickRef.current = refreshTick;
      listRef.current?.scrollToOffset?.({ offset: 0, animated: false });
    }
  }, [refreshTick]);

  const resolvedRenderItem = renderItem
    ? renderItem
    : ({ data: item, index }) => null;

  const resolvedKeyExtractor = keyExtractor
    ? keyExtractor
    : (item) =>
        String(
          item._id != null
            ? item._id
            : item.id != null
            ? item.id
            : Math.random(),
        );

  const handleEndReached = React.useCallback(() => {
    if (
      hasNextPage &&
      !loadingLoadMore &&
      !refetching &&
      typeof onLoadMore === 'function'
    ) {
      onLoadMore();
    }
  }, [hasNextPage, loadingLoadMore, refetching, onLoadMore]);

  const handleRefresh = React.useCallback(async () => {
    if (typeof onRefresh !== 'function') {
      return;
    }
    setRefreshing(true);
    try {
      await Promise.resolve(onRefresh());
    } catch (_e) {}
    setRefreshing(false);
    // Pull-to-refresh returns to the top (a plain refetch doesn't bump refreshTick).
    listRef.current?.scrollToOffset?.({ offset: 0, animated: false });
  }, [onRefresh]);

  const [searchKeyword, setSearchKeyword] = React.useState(searchValue || '');
  React.useEffect(() => {
    setSearchKeyword(searchValue || '');
  }, [searchValue]);

  // Optional "search for" field selector (e.g. Sales Order: order number / customer
  // name / code / PO). Its value is folded into the dispatched filter as `search_for`,
  // matching the web Table; the screen's toFilter maps it to keyword_for.
  const [searchForKeyword, setSearchForKeyword] = React.useState(
    searchForValue || '',
  );
  React.useEffect(() => {
    if (searchForValue !== undefined && searchForValue !== null) {
      setSearchForKeyword(searchForValue);
    }
  }, [searchForValue]);

  // Search dispatches to scoped params (filterName); the screen's query arg then
  // changes and RTK refetches — same contract as the web Table / legacy mobile path.
  const handleSearch = React.useCallback(
    debounce((keyword, forValue) => {
      const oldFilter = appliedParams?.filter || {};
      const new_filter = { ...oldFilter };
      if (Array.isArray(searchKey)) {
        // multi-field search → Mongo $or (matches the web Table behavior)
        if (keyword) {
          new_filter.$or = searchKey.map((field) => ({
            [field]: { $regex: keyword, $options: 'i' },
          }));
        } else {
          delete new_filter.$or;
        }
      } else if (keyword) {
        new_filter[searchKey] = { $regex: keyword, $options: 'i' };
      } else {
        delete new_filter[searchKey];
      }
      // Field selector → search_for. Kept as long as a field is selected (matches the
      // web Table), so clearing the keyword doesn't reset the selector to its default.
      if (forValue) {
        new_filter.search_for = forValue;
      } else {
        delete new_filter.search_for;
      }
      dispatch(
        paginationAdded({
          key: filterName,
          skip: 0,
          limit: appliedParams?.limit,
        }),
      );
      dispatch(filterAdded({ key: filterName, filter: new_filter }));
    }, 500),
    [appliedParams, filterName, searchKey, dispatch],
  );

  const searchBar =
    showSearch && searchKey ? (
      <SearchInputMobile
        searchKey={searchKey}
        placeholder={placeholder || 'Search something...'}
        value={searchKeyword}
        valueFor={searchForKeyword}
        searchForOptions={searchForOptions}
        onSearch={(value, valueFor) => {
          setSearchKeyword(value);
          if (valueFor !== undefined) {
            setSearchForKeyword(valueFor);
          }
          handleSearch(value, valueFor);
        }}
        filterName={filterName}
      />
    ) : null;

  const rows = Array.isArray(data) ? data : [];
  const isEmpty = !loading && rows.length === 0;

  const ListFooter = React.useCallback(() => {
    if (loadingLoadMore) {
      return (
        <View style={{ width: '100%', paddingVertical: 20 }}>
          <ActivityIndicator size="large" color={Colors.blue0} />
        </View>
      );
    }
    if (!hasNextPage && rows.length > 0) {
      return (
        <View style={endOfDataStyles.wrapper}>
          <View style={endOfDataStyles.line} />
        </View>
      );
    }
    return <View style={{ width: '100%', height: 80 }} />;
  }, [loadingLoadMore, hasNextPage, rows.length]);

  // Single render tree: the search bar stays MOUNTED in the same position across
  // loading / empty / list states, so the input keeps focus and the keyboard does
  // not dismiss when a search returns no results. Only the content below swaps.
  return (
    <View testID={testID || 'table-mobile-infinite'} style={styles.wrapper}>
      <View onLayout={(e) => setSearchHeight(e.nativeEvent.layout.height)}>
        {searchBar}
      </View>
      {loading && rows.length === 0 ? (
        <View style={{ width: '100%', paddingVertical: 15 }}>
          {headerComponent || null}
          <ActivityIndicator size="large" color={Colors.blue0} />
        </View>
      ) : isEmpty ? (
        <>
          {headerComponent || null}
          <View
            style={{
              width: '100%',
              paddingHorizontal: 15,
              paddingVertical: 25,
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <SociollaText style={{ fontSize: 13 }}>No data found</SociollaText>
          </View>
        </>
      ) : (
        <LegendList
          ref={listRef}
          data={rows}
          estimatedItemSize={100}
          keyExtractor={resolvedKeyExtractor}
          renderItem={({ item, index }) =>
            resolvedRenderItem({ data: item, index })
          }
          recycleItems={!isAndroid}
          // LegendList defaults maintainVisibleContentPosition to true, which
          // mis-calculates on data reload (refetch) and jumps the list to the
          // bottom on Android. We only ever append (load-more), never prepend, so
          // we don't need it — disable to keep scroll position stable.
          maintainVisibleContentPosition={false}
          maintainScrollAtEnd={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
          ListHeaderComponent={headerComponent || null}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          ListFooterComponent={ListFooter}
          contentContainerStyle={{ paddingBottom: 20 }}
          style={
            isMobileWeb
              ? { height: Math.max(mobileWebListHeight - searchHeight, 0) }
              : { flex: 1 }
          }
        />
      )}
      {/* Background refetch (search/filter) — keep rows visible, dim + spinner.
          Suppressed during first load, load-more, and pull-to-refresh. */}
      {refetching &&
      !loading &&
      !loadingLoadMore &&
      !refreshing &&
      rows.length > 0 ? (
        // pointerEvents="none" → visual indicator only; never blocks the scroll
        // gesture (otherwise the list can't be scrolled while a refetch is in flight).
        <View style={styles.refetchOverlay} pointerEvents="none">
          <ActivityIndicator size="large" color={Colors.blue0} />
        </View>
      ) : null}
    </View>
  );
};
// ─── END TableMobileInfinite ──────────────────────────────────────────────────

const TableMobile = (props) => {
  // ─── NEW infinite-scroll path ───────────────────────────────────────────────
  // Activated ONLY when caller passes `data` (flattened RTK infinite-query rows).
  // All legacy consumers pass `tableData`, never `data`, so this block is fully
  // additive — existing behaviour is untouched.
  const isInfiniteMode = Array.isArray(props.data);

  if (isInfiniteMode) {
    return (
      <TableMobileInfinite
        data={props.data}
        renderItem={props.renderItem}
        onLoadMore={props.onLoadMore}
        hasNextPage={props.hasNextPage}
        loadingLoadMore={props.loadingLoadMore}
        loading={props.loading}
        refetching={props.refetching}
        onRefresh={props.onRefresh}
        keyExtractor={props.keyExtractor}
        totalData={props.totalData}
        headerComponent={props.headerComponent}
        searchProps={props.searchProps}
        testID={props.testID}
        filterName={props.filterName}
        searchKey={props.searchKey}
        searchValue={props.searchValue}
        searchForOptions={props.searchForOptions}
        searchForValue={props.searchForValue}
        placeholder={props.placeholder}
        showSearch={props.showSearch}
      />
    );
  }
  // ─── END new path ────────────────────────────────────────────────────────────

  const REFRESH_CHUNK_LIMIT = 15;
  const REFRESH_DELAY = 500;
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const mobileWebListHeight =
    Dimensions.get('window').height -
    insets.top -
    insets.bottom -
    MOBILE_HEADER_HEIGHT;
  const filterName = props.filterName ? props.filterName : null;
  const paramsState = useSelector(selectParams(filterName)); // existing parameters for filter
  const localParams = props.localParams ? props.localParams : null;
  const hasLocalParams = localParams ? true : false;
  const currentParams = hasLocalParams ? localParams : paramsState;
  const hasFilter =
    currentParams?.filter && Object.keys(currentParams?.filter).length > 0; // has filter value
  // const [skip, setSkip] = useState(
  //   Number(props.skip && !isNaN(props.skip) ? props.skip : 0),
  // ); // skip for pagination
  const skip = Number(
    !isNaN(props.skip)
      ? props.skip
      : !isNaN(currentParams?.skip)
      ? currentParams?.skip
      : 0,
  );
  const [scrolled, setScrolled] = useState(false); // if you have scroll the component
  const searchKey = props.searchKey ? props.searchKey : ''; // search key for filter
  const withFetch = props.withFetch == false ? false : true; // have fetch api on outside component
  const showSearch = props.showSearch == false ? false : true;
  const noResetOnRefresh = props.noResetOnRefresh ? true : false;
  const tableData = props.tableData?.length ? props.tableData : [];
  const totalData = Number(
    props.totalData && !isNaN(props.totalData) ? props.totalData : 0,
  ); // total data
  const limit = Number(
    !isNaN(props.limit)
      ? props.limit
      : !isNaN(currentParams?.limit)
      ? currentParams?.limit
      : 15,
  ); // limit for pagination
  const calculatePage = Math.round(totalData / limit); // calculate total page we have
  const pageCount = calculatePage ? calculatePage : 1; // final total page
  const calculatePageNumber = skip / limit + 1; // calculate active page number
  const pageNumber = calculatePageNumber ? calculatePageNumber : 1; // final active page number
  const hasChildren = props.hasChildren ? props.hasChildren : false; // if the data process on parent
  const headerComponent = props.headerComponent ? props.headerComponent : false;
  const renderItem = props.renderItem
    ? props.renderItem
    : ({ data, index }) => {}; // render component
  const renderFooterItem = props.footerComponent
    ? props.footerComponent
    : false; // render footer item
  const renderFooterStatic = props.renderFooterStatic
    ? props.renderFooterStatic
    : false; // render another footer
  const notRenderFooter = props.notRenderFooter ? props.notRenderFooter : false;
  const hasEmptyAction = props.hasEmptyAction ? props.hasEmptyAction : false;
  const parsedTableHeight = Number(props.tableHeight);
  const tableHeight = Number.isFinite(parsedTableHeight)
    ? parsedTableHeight
    : 0;
  const searchForOptions = props.searchForOptions
    ? props.searchForOptions
    : false;
  const extraData = props.extraData ? props.extraData : undefined;

  const loading = props.loading ? props.loading : false; // loading initial
  const loadingPagination = props.loadingPagination
    ? props.loadingPagination
    : false; // loading for pagination process
  // Background refetch on a paginated table: keep the rows, dim them, overlay a spinner — what
  // Table does on web. Opt-in and default false, so the other TableMobile callers are unchanged;
  // `loadingPagination` alone drives the infinite-scroll footer spinner, which is a different
  // thing and stays as it is.
  const refetching = props.refetching ? true : false;
  const delay = props.delay ? Number(props.delay) : 500;
  const isSmall = props.isSmall ? props.isSmall : false;
  // Rows render inline and the enclosing screen scrolls: for a table embedded in a card
  // rather than filling the screen. Implies the plain (non-virtualised) list, since a
  // virtualised one needs a bounded height of its own.
  const noScroll = props.noScroll ? true : false;
  const useLegendList = noScroll
    ? false
    : typeof props.useLegendList === 'boolean'
    ? props.useLegendList
    : isNative;
  const scrollRef = props.scrollRef ? props.scrollRef : () => {};
  const children = props.children ? props.children : false;
  const withLoadMore = props.withLoadMore ? props.withLoadMore : false;
  // Numbered pages in the footer instead of load-more. Moves the offset the same way
  // onLoadMore does, then reports it through onSetSkip.
  const withPagination = props.withPagination ? true : false;
  const [mobileWebScrollHeight, setMobileWebScrollHeight] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState(
    props.searchValue ? props.searchValue : '',
  );
  const [searchForKeyword, setSearchForKeyword] = useState(
    props.searchForValue ? props.searchForValue : '',
  );

  React.useEffect(() => {
    if (
      props.searchValue !== undefined &&
      props.searchValue !== null &&
      props.searchValue !== ''
    ) {
      setSearchKeyword(props.searchValue);
    } else if (props.searchValue === '') {
      setSearchKeyword('');
    }
  }, [props.searchValue]);

  React.useEffect(() => {
    if (props.searchForValue !== undefined && props.searchForValue !== null) {
      setSearchForKeyword(props.searchForValue);
    }
  }, [props.searchForValue]);

  // onfocus callback search input
  const onFocusSearch = () => {
    try {
      props.onFocus();
    } catch (error) {}
  };

  // onvlur callback search input
  const onBlurSearch = () => {
    try {
      props.onBlur();
    } catch (error) {}
  };

  // on search callback
  const onSearch = useCallback(
    debounce((keyword, keywordFor) => {
      try {
        if (hasLocalParams && props.onSearchTable) {
          props.onSearchTable({ keyword, keywordFor }, 'keyword');
        } else if (withFetch) {
          const oldParams = getCurrentParams(store.getState(), filterName);
          const oldFilter = oldParams?.filter || {};
          const new_filter = {
            ...oldFilter,
            [searchKey]: { $regex: keyword, $options: 'i' },
            search_for: keywordFor,
          };
          if (!keyword) {
            delete new_filter[searchKey];
          }
          if (!keywordFor) {
            delete new_filter.search_for;
          }
          dispatch(paginationAdded({ key: filterName, skip: 0, limit: limit }));
          dispatch(filterAdded({ key: filterName, filter: new_filter }));
          setSavedFilter({ filters: new_filter });
        }
        if (props.onSearch) {
          props.onSearch(keyword, keywordFor);
        }
      } catch (error) {}
    }, delay),
    [hasLocalParams, props.onSearchTable, withFetch, searchKey, limit, delay],
  );

  // if you are scrolled page into bottom
  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }) => {
    const paddingToBottom = 50;
    return (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    );
  };

  // load more callback
  const onLoadMore = () => {
    if (!loadingPagination) {
      if (pageCount >= pageNumber && totalData > limit) {
        setScrolled(true);
        const newSkip = skip + limit;
        // setSkip(newSkip);
        try {
          if (hasLocalParams && props.onSearchTable) {
            props.onSearchTable({ skip: newSkip, limit: limit }, 'pagination');
          } else if (withFetch) {
            dispatch(
              paginationAdded({ key: filterName, skip: newSkip, limit: limit }),
            );
          }
          if (props.onLoadMore) {
            props.onLoadMore(newSkip);
          }
        } catch (error) {}
      } else {
        setScrolled(false);
      }
    }
  };

  const onSetSkip = (newSkip) => {
    if (loadingPagination) {
      return;
    }
    try {
      if (hasLocalParams && props.onSearchTable) {
        props.onSearchTable({ skip: newSkip, limit: limit }, 'pagination');
      } else if (withFetch) {
        dispatch(
          paginationAdded({ key: filterName, skip: newSkip, limit: limit }),
        );
      }
      if (props.onSetSkip) {
        props.onSetSkip(newSkip);
      }
    } catch (error) {}
  };

  // reset all filter that we have
  const onResetFilter = () => {
    if (hasLocalParams && props.onSearchTable) {
      props.onSearchTable({ skip: 0, limit: limit }, 'reset');
    } else if (withFetch) {
      dispatch(paramsReseted({ key: filterName }));
    }
    setTimeout(() => {
      if (props.onSearch) {
        props.onSearch();
      }
    }, 500);
  };

  const onRefresh = async () => {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const triggerSearch = async () => {
      try {
        if (typeof props.onSearch === 'function') {
          await Promise.resolve(props.onSearch());
        }
      } catch (error) {}
    };
    const triggerLocalSearchTable = async (payload, type) => {
      try {
        if (typeof props.onSearchTable === 'function') {
          await Promise.resolve(props.onSearchTable(payload, type));
        }
      } catch (error) {}
    };
    const triggerLoadMore = async (newSkip) => {
      try {
        if (typeof props.onLoadMore === 'function') {
          await Promise.resolve(props.onLoadMore(newSkip));
        } else {
          await triggerSearch();
        }
      } catch (error) {}
    };

    setScrolled(false);
    setSearchKeyword('');

    if (hasLocalParams && props.onSearchTable) {
      if (!hasChildren && noResetOnRefresh) {
        const currentSkip = Number(currentParams?.skip || 0);
        const requestedTotal = currentSkip > 0 ? currentSkip + limit : limit;

        if (requestedTotal <= REFRESH_CHUNK_LIMIT) {
          await triggerLocalSearchTable(
            { skip: 0, limit: requestedTotal },
            'pagination',
          );
          return;
        }

        const firstChunkLimit = Math.min(REFRESH_CHUNK_LIMIT, requestedTotal);
        await triggerLocalSearchTable(
          { skip: 0, limit: firstChunkLimit },
          'pagination',
        );

        let refreshed = firstChunkLimit;
        while (refreshed < requestedTotal) {
          const chunkLimit = Math.min(
            REFRESH_CHUNK_LIMIT,
            requestedTotal - refreshed,
          );
          await triggerLocalSearchTable(
            { skip: refreshed, limit: chunkLimit },
            'pagination',
          );
          refreshed += chunkLimit;
        }

        return;
      }

      await triggerLocalSearchTable({ skip: 0, limit: limit }, 'reset');
      return;
    }

    if (!withFetch) {
      await wait(REFRESH_DELAY);
      await triggerSearch();
      return;
    }

    if (!hasChildren && noResetOnRefresh) {
      const params = getCurrentParams(store.getState(), filterName);
      const currentSkip = Number(params?.skip || 0);
      const requestedTotal = currentSkip > 0 ? currentSkip + limit : limit;

      if (requestedTotal <= REFRESH_CHUNK_LIMIT) {
        dispatch(
          paginationAdded({ key: filterName, skip: 0, limit: requestedTotal }),
        );
        await wait(REFRESH_DELAY);
        await triggerSearch();
        return;
      }

      const firstChunkLimit = Math.min(REFRESH_CHUNK_LIMIT, requestedTotal);
      dispatch(
        paginationAdded({ key: filterName, skip: 0, limit: firstChunkLimit }),
      );
      await wait(REFRESH_DELAY);
      await triggerSearch();

      let refreshed = firstChunkLimit;
      while (refreshed < requestedTotal) {
        const chunkLimit = Math.min(
          REFRESH_CHUNK_LIMIT,
          requestedTotal - refreshed,
        );
        dispatch(
          paginationAdded({
            key: filterName,
            skip: refreshed,
            limit: chunkLimit,
          }),
        );
        await wait(REFRESH_DELAY);
        await triggerLoadMore(refreshed);
        refreshed += chunkLimit;
      }

      dispatch(
        paginationAdded({
          key: filterName,
          skip: Math.max(requestedTotal - limit, 0),
          limit: limit,
        }),
      );
      return;
    }

    dispatch(paramsReseted({ key: filterName }));
    await wait(REFRESH_DELAY);
    await triggerSearch();
  };

  const SearchWrapper = useMemo(() => {
    return showSearch ? (
      <SearchInputMobile
        searchKey={searchKey}
        placeholder={
          props.placeholder ? props.placeholder : 'Search something...'
        }
        onFocus={() => onFocusSearch()}
        onBlur={() => onBlurSearch()}
        value={searchKeyword}
        valueFor={searchForKeyword}
        onSearch={async (value, valueFor) => {
          await setSearchKeyword(value);
          await setSearchForKeyword(valueFor);
          onSearch(value, valueFor);
        }}
        searchForOptions={searchForOptions}
        onScan={props.searchInputProps?.onScan}
        scannedEAN={props.searchInputProps?.scannedEAN}
        onRemoveScannedEAN={props.searchInputProps?.onRemoveScannedEAN}
        filterName={filterName}
      />
    ) : (
      false
    );
  }, [
    showSearch,
    searchKey,
    searchKeyword,
    searchForOptions,
    searchForKeyword,
    props.searchInputProps?.onScan,
    props.searchInputProps?.scannedEAN,
    props.searchInputProps?.onRemoveScannedEAN,
  ]);

  const PaginationFooter = withPagination ? (
    <View style={paginationStyles.wrapper}>
      <Pagination
        testID={`${props.testID || 'table-mobile'}-pagination`}
        totalData={totalData}
        skip={skip}
        limit={limit}
        showFirstLast
        showPageNumbers={false}
        onSetSkip={onSetSkip}
      />
    </View>
  ) : (
    false
  );

  const ListWrapper = useMemo(() => {
    return loading && !scrolled ? (
      // Loading Component
      <View style={{ width: '100%', paddingVertical: 15 }}>
        <ActivityIndicator size="large" color={Colors.blue0} />
      </View>
    ) : !loading &&
      !renderFooterItem &&
      !renderFooterStatic &&
      !(hasChildren
        ? Array.isArray(children)
          ? children.length
          : children
        : tableData?.length) ? (
      // If data is not found or no data
      <View
        style={{
          width: '100%',
          paddingHorizontal: 15,
          paddingVertical: 25,
          flexDirection: hasFilter || hasEmptyAction ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <SociollaText style={{ fontSize: 13 }}>No data found</SociollaText>
        {hasFilter ? (
          <Button
            type="default"
            rounded
            buttonStyle={{ width: 120, marginTop: 15 }}
            title={
              <SociollaText type="medium" style={{ fontSize: 13 }}>
                Reset Filter
              </SociollaText>
            }
            onPress={() => onResetFilter()}
          />
        ) : (
          false
        )}
        {hasEmptyAction ? hasEmptyAction : false}
      </View>
    ) : (
      // if we have some data
      <View style={{ width: '100%', position: 'relative', flex: 1 }}>
        {useLegendList ? (
          <LegendList
            data={
              hasChildren
                ? Array.isArray(children)
                  ? children
                  : children
                  ? [children]
                  : []
                : tableData
            }
            estimatedItemSize={100}
            keyExtractor={(item, index) =>
              hasChildren ? index.toString() : item?._id || index.toString()
            }
            renderItem={({ item, index }) =>
              hasChildren ? item : renderItem({ data: item, index })
            }
            recycleItems={!isAndroid}
            refreshControl={
              <RefreshControl refreshing={loading} onRefresh={onRefresh} />
            }
            ListHeaderComponent={headerComponent}
            onEndReached={withLoadMore ? onLoadMore : null}
            onEndReachedThreshold={0.5}
            extraData={extraData}
            ListFooterComponent={
              <>
                {scrolled && loadingPagination ? (
                  <View style={{ width: '100%', paddingVertical: 20 }}>
                    <ActivityIndicator size="large" color={Colors.blue0} />
                  </View>
                ) : null}
                {withLoadMore &&
                !loadingPagination &&
                tableData.length > 0 &&
                tableData.length >= totalData ? (
                  <View style={endOfDataStyles.wrapper}>
                    <View style={endOfDataStyles.line} />
                  </View>
                ) : null}
                {PaginationFooter}
                {renderFooterStatic ? renderFooterStatic : false}
                {typeof renderFooterItem == 'boolean' &&
                !notRenderFooter &&
                !isMobileWeb ? (
                  <View
                    style={{
                      width: '100%',
                      height: searchForOptions ? 160 : 80,
                    }}></View>
                ) : null}
              </>
            }
            contentContainerStyle={{
              paddingBottom: 20,
            }}
            style={
              tableHeight
                ? { maxHeight: tableHeight }
                : isMobileWeb
                ? { height: mobileWebListHeight }
                : { flex: 1 }
            }
            ref={(val) => scrollRef(val)}
          />
        ) : (
          <>
            {headerComponent ? headerComponent : false}
            <ScrollViewRefresher
              innerRef={(val) => scrollRef(val)}
              isTable
              noScroll={noScroll}
              showSearch={showSearch}
              isFullHeight={!isSmall && !isMobileWeb}
              style={
                isMobileWeb && mobileWebScrollHeight
                  ? { height: mobileWebScrollHeight }
                  : {}
              }
              scrollEventThrottle={16}
              maxHeight={tableHeight}
              hasHeader={headerComponent ? true : false}
              hasFooter={renderFooterItem ? true : false}
              onCallback={() => {
                onRefresh();
              }}
              onScroll={({ nativeEvent }) => {
                if (withLoadMore && isCloseToBottom(nativeEvent)) {
                  onLoadMore();
                }
              }}>
              {hasChildren
                ? Array.isArray(children)
                  ? children.map((child, idx) => (
                      <React.Fragment key={`child-${idx}`}>
                        {child}
                      </React.Fragment>
                    ))
                  : children
                : tableData.map((item, idx) => (
                    <React.Fragment
                      key={
                        item?._id ||
                        item?.id ||
                        item?.key ||
                        `table-item-${idx}`
                      }>
                      {renderItem({ data: item, index: idx })}
                    </React.Fragment>
                  ))}
              {/* Start Pagination Loader */}
              {scrolled && loadingPagination ? (
                <View style={{ width: '100%', paddingVertical: 20 }}>
                  <ActivityIndicator size="large" color={Colors.blue0} />
                </View>
              ) : (
                false
              )}
              {withLoadMore &&
              !loadingPagination &&
              tableData.length > 0 &&
              tableData.length >= totalData ? (
                <View style={endOfDataStyles.wrapper}>
                  <View style={endOfDataStyles.line} />
                </View>
              ) : (
                false
              )}
              {PaginationFooter}
              {renderFooterStatic ? renderFooterStatic : false}
              {/* End Pagination Loader */}
              {typeof renderFooterItem == 'boolean' &&
              !notRenderFooter &&
              !isMobileWeb ? (
                <View
                  style={{
                    width: '100%',
                    height: searchForOptions ? 160 : 80,
                  }}></View>
              ) : (
                false
              )}
            </ScrollViewRefresher>
          </>
        )}
        {/* Footer Components */}
        <View style={{ width: '100%' }}>
          {renderFooterItem ? renderFooterItem : false}
        </View>
        {/* Paginated refetch: dim the page that is still on screen rather than replacing it. */}
        {refetching && withPagination && tableData.length ? (
          <View style={refetchOverlayStyles.overlay}>
            <ActivityIndicator size="large" color={Colors.blue0} />
          </View>
        ) : null}
      </View>
    );
  }, [
    loading,
    loadingPagination,
    refetching,
    children,
    renderItem,
    isSmall,
    withLoadMore,
    scrolled,
    hasChildren,
    hasEmptyAction,
    hasFilter,
    showSearch,
    tableData,
    headerComponent,
    renderFooterStatic,
    renderFooterItem,
    notRenderFooter,
    searchForOptions,
    tableHeight,
    useLegendList,
    extraData,
    mobileWebScrollHeight,
    totalData,
    withPagination,
    skip,
    limit,
    props.onSetSkip,
  ]);

  return (
    <View testID={props.testID || 'table-mobile'} style={styles.wrapper}>
      {SearchWrapper}
      <View
        style={{ flex: 1 }}
        onLayout={
          isMobileWeb
            ? (e) => {
                const h = e.nativeEvent.layout.height;
                if (h > 0) {
                  setMobileWebScrollHeight(h);
                }
              }
            : undefined
        }>
        {ListWrapper}
      </View>
    </View>
  );
};

const paginationStyles = StyleSheet.create({
  wrapper: { alignItems: 'flex-end', paddingTop: 12, width: '100%' },
});

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    width: '100%',
    backgroundColor: Colors.white,
  },
  refetchOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.whiteOverlay,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
});

// Mirrors Table's refetchOverlay on web, so a paginated table behaves the same on both.
const refetchOverlayStyles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.whiteOverlay,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
});

const endOfDataStyles = StyleSheet.create({
  wrapper: {
    paddingBottom: 20,
    paddingHorizontal: 16,
  },
  line: {
    height: 1,
    backgroundColor: Colors.grey0,
  },
});

export default React.memo(TableMobile, (prev, next) => prev == next);
